// Physician progress notes — SOAP, with the signing and amendment rules made
// visible rather than merely enforced.
//
// ── What the UI has to get right here ────────────────────────────
//
// A signed note has no edit affordance at all. Not a disabled one — none. The
// only way to change it is "Amend", which is labelled as what it does: create
// a new version and keep the original. If someone reaches for Edit on a signed
// note, the interface has already failed to communicate the rule.
//
// An AI-drafted note is visually marked and cannot be signed from the list —
// it has to be opened first. AI never finalizes a clinical note.
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Textarea, NativeSelect } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { ApiError } from '@/lib/api/client';
import { mergeTextFields, vOneOf, type Extracted } from '@/lib/voiceFill';
import { useAuth } from '@/hooks/useAuth';
import {
  useProgressNotes, useCreateProgressNote, useUpdateProgressNote,
  useSignProgressNote, useAmendProgressNote,
} from '../hooks';
import type { ProgressNote, ProgressNoteInput, SymptomTrend } from '../types';
import { Section, Empty, NoteStatusBadge, fmtDateTime, useCan, BackdateField } from '../ui';

const EMPTY: ProgressNoteInput = {
  subjective: '', overnight_events: '', symptom_trend: 'unknown',
  objective: '', investigation_summary: '',
  assessment: '', clinical_impression: '', plan: '',
};

/**
 * The editable subset of a note, for seeding an edit or amendment draft.
 *
 * Mirrors the server's own field whitelist: `status`, `signed_by`, the AI
 * provenance columns and the amendment chain are NOT editable, so they must
 * not ride along in a draft where a stray spread could resubmit them.
 */
function toDraft(note: ProgressNote): ProgressNoteInput {
  return {
    note_datetime: note.note_datetime,
    note_type: note.note_type,
    subjective: note.subjective,
    overnight_events: note.overnight_events,
    symptom_trend: note.symptom_trend,
    objective: note.objective,
    examination_findings: note.examination_findings,
    investigation_summary: note.investigation_summary,
    assessment: note.assessment,
    active_problems: note.active_problems,
    clinical_impression: note.clinical_impression,
    plan: note.plan,
    plan_items: note.plan_items,
  };
}

// One dictation covering the whole note ("patient reports...", "on exam...",
// "impression is...", "plan is...") rather than a mic per field — matches
// how a SOAP note is actually dictated aloud.
const SOAP_VOICE_FIELDS: VoiceField[] = [
  { key: 'subjective', label: 'Subjective', type: 'text' },
  { key: 'overnight_events', label: 'Overnight events', type: 'text' },
  { key: 'symptom_trend', label: 'Symptom trend', type: 'select', options: ['improving', 'unchanged', 'worsening', 'unknown'] },
  { key: 'objective', label: 'Objective', type: 'text' },
  { key: 'investigation_summary', label: 'Investigation summary', type: 'text' },
  { key: 'assessment', label: 'Assessment', type: 'text' },
  { key: 'clinical_impression', label: 'Clinical impression', type: 'text' },
  { key: 'plan', label: 'Plan', type: 'text' },
];

function SoapForm({ value, onChange }: { value: ProgressNoteInput; onChange: (v: ProgressNoteInput) => void }) {
  const set = (k: keyof ProgressNoteInput, v: unknown) => onChange({ ...value, [k]: v });
  const block = (label: string, hint: string, children: React.ReactNode) => (
    <div>
      <div className="mb-1 flex items-baseline gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-teal">{label}</span>
        <span className="text-[11px] text-ink-muted">{hint}</span>
      </div>
      {children}
    </div>
  );

  const applyVoice = (x: Extracted) => {
    const trend = vOneOf(x, 'symptom_trend', ['improving', 'unchanged', 'worsening', 'unknown'] as const);
    onChange({
      ...value,
      ...mergeTextFields(value, x, ['subjective', 'overnight_events', 'objective', 'investigation_summary', 'assessment', 'clinical_impression', 'plan']),
      ...(trend ? { symptom_trend: trend } : {}),
    });
  };

  return (
    <div className="space-y-3">
      <VoiceFormFiller fields={SOAP_VOICE_FIELDS} onFields={applyVoice} label="🎙 Dictate this note"
        hint="Feeling better, no fever overnight; chest clear, abdomen soft; impression resolving pneumonia; plan continue antibiotics, step down to oral tomorrow" />

      {block('Subjective', 'what the patient reports, overnight events', (
        <div className="space-y-2">
          <Textarea rows={2} value={value.subjective ?? ''} onChange={e => set('subjective', e.target.value)}
            placeholder="Symptoms, complaints, how the patient feels" />
          <Input value={value.overnight_events ?? ''} onChange={e => set('overnight_events', e.target.value)}
            placeholder="Overnight events" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-muted">Trend</span>
            <NativeSelect className="h-7 w-auto text-xs"
              value={value.symptom_trend ?? 'unknown'}
              onChange={e => set('symptom_trend', e.target.value as SymptomTrend)}>
              {['improving', 'unchanged', 'worsening', 'unknown'].map(t => <option key={t} value={t}>{t}</option>)}
            </NativeSelect>
          </div>
        </div>
      ))}

      {block('Objective', 'examination and results — vitals are read from the chart, not retyped', (
        <div className="space-y-2">
          <Textarea rows={2} value={value.objective ?? ''} onChange={e => set('objective', e.target.value)}
            placeholder="Examination findings" />
          <Input value={value.investigation_summary ?? ''} onChange={e => set('investigation_summary', e.target.value)}
            placeholder="Relevant investigation results" />
        </div>
      ))}

      {block('Assessment', 'clinical impression and problem status', (
        <div className="space-y-2">
          <Textarea rows={2} value={value.assessment ?? ''} onChange={e => set('assessment', e.target.value)}
            placeholder="Assessment of the active problems" />
          <Input value={value.clinical_impression ?? ''} onChange={e => set('clinical_impression', e.target.value)}
            placeholder="Overall impression" />
        </div>
      ))}

      {block('Plan', 'what happens next', (
        <Textarea rows={2} value={value.plan ?? ''} onChange={e => set('plan', e.target.value)}
          placeholder="Medication changes, investigations, monitoring, discharge planning" />
      ))}

      <BackdateField value={value.note_datetime} onChange={v => set('note_datetime', v)} label="Note dated" />
    </div>
  );
}

function NoteCard({ note, admissionId, readOnly }: {
  note: ProgressNote; admissionId: number; readOnly: boolean;
}) {
  const { user } = useAuth();
  const can = useCan();
  const update = useUpdateProgressNote(admissionId);
  const sign = useSignProgressNote(admissionId);
  const amend = useAmendProgressNote(admissionId);

  const [editing, setEditing] = useState(false);
  const [amending, setAmending] = useState(false);
  const [draft, setDraft] = useState<ProgressNoteInput>(() => toDraft(note));
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mayWrite = can('ip:progress-note') && !readOnly;
  // A note is signed by its author. Not delegable — a signature someone else
  // can apply is not a signature.
  const isAuthor = !note.created_by_user_id || note.created_by_user_id === (user as { id?: number } | null)?.id;
  const isDraft = note.status === 'draft';

  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');

  return (
    <div data-focus={`progress_note:${note.id}${isDraft ? ' progress_note:unsigned' : ''}`} className="rounded-lg border border-border p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold">{fmtDateTime(note.note_datetime)}</span>
          <NoteStatusBadge status={note.status} source={note.source} />
          {note.version > 1 && <Badge variant="neutral">v{note.version}</Badge>}
          {note.symptom_trend && note.symptom_trend !== 'unknown' && (
            <Badge variant={note.symptom_trend === 'improving' ? 'success'
              : note.symptom_trend === 'worsening' ? 'danger' : 'neutral'}>
              {note.symptom_trend}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {mayWrite && isDraft && !editing && (
            <Button variant="ghost" size="sm" onClick={() => { setDraft(toDraft(note)); setEditing(true); }}>Edit</Button>
          )}
          {mayWrite && isDraft && isAuthor && (
            <Button size="sm" onClick={() => sign.mutate(note.id, { onError: fail })} loading={sign.isPending}>
              Sign
            </Button>
          )}
          {/* No Edit at all on a signed note — only Amend, labelled as what it does. */}
          {mayWrite && note.status === 'signed' && !note.superseded_by_note_id && !amending && (
            <Button variant="ghost" size="sm" onClick={() => { setDraft(toDraft(note)); setAmending(true); }}>
              Amend
            </Button>
          )}
        </div>
      </div>

      {note.source === 'ai_assisted' && isDraft && (
        <div className="mb-2 rounded border border-info/40 bg-info-soft px-2.5 py-1.5 text-[11.5px] text-info-ink">
          AI-drafted from the record. Review every line before signing — signing makes it your note.
          {note.ai_model ? ` (${note.ai_provider}/${note.ai_model})` : ''}
        </div>
      )}

      {note.amendment_reason && (
        <div className="mb-2 rounded border border-warn/40 bg-warn-soft px-2.5 py-1.5 text-[11.5px] text-warn-ink">
          Amendment of note #{note.amends_note_id}: {note.amendment_reason}
          {note.amended_by ? ` — ${note.amended_by}` : ''}
        </div>
      )}
      {note.superseded_by_note_id && (
        <div className="mb-2 rounded bg-muted px-2.5 py-1.5 text-[11.5px] text-ink-muted">
          Superseded by note #{note.superseded_by_note_id}. Kept unchanged as the original record.
        </div>
      )}

      {/* Sign (above) runs while neither editing nor amending is true — the
          {err} render further down, inside that branch, never showed a
          Sign failure (e.g. "Add your registration number…", 422), which
          made a refused sign look like clicking Sign did nothing. */}
      {!editing && !amending && err && (
        <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>
      )}

      {editing || amending ? (
        <div className="space-y-3">
          {amending && (
            <div>
              <label className="mb-1 block text-xs font-semibold">
                Reason for amendment <span className="font-normal text-ink-muted">(required)</span>
              </label>
              <Input value={reason} onChange={e => setReason(e.target.value)}
                placeholder="e.g. corrected respiratory rate" />
              <p className="mt-1 text-[11px] text-ink-muted">
                This creates a new version. The original stays on record, unchanged.
              </p>
            </div>
          )}
          <SoapForm value={draft} onChange={setDraft} />
          {err && <div className="rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setAmending(false); setErr(null); }}>
              Cancel
            </Button>
            <Button size="sm"
              loading={update.isPending || amend.isPending}
              disabled={amending && !reason.trim()}
              onClick={() => {
                setErr(null);
                if (amending) {
                  amend.mutate({ noteId: note.id, reason: reason.trim(), changes: draft },
                    { onSuccess: () => { setAmending(false); setReason(''); }, onError: fail });
                } else {
                  update.mutate({ noteId: note.id, input: draft },
                    { onSuccess: () => setEditing(false), onError: fail });
                }
              }}>
              {amending ? 'Save amendment' : 'Save draft'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-2 text-[12.5px] sm:grid-cols-2">
          {([['Subjective', note.subjective], ['Objective', note.objective],
             ['Assessment', note.assessment], ['Plan', note.plan]] as const).map(([label, text]) => (
            text ? (
              <div key={label}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-muted">{label}</div>
                <div className="whitespace-pre-wrap">{text}</div>
              </div>
            ) : null
          ))}
        </div>
      )}

      <div className="mt-2 text-[11px] text-ink-muted">
        {note.status === 'signed'
          ? `Signed by ${note.signed_by ?? 'unknown'} · ${fmtDateTime(note.signed_at)}`
          : `Drafted by ${note.author_name || note.created_by || 'unknown'}`}
      </div>
    </div>
  );
}

export function ProgressSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const [showHistory, setShowHistory] = useState(false);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState<ProgressNoteInput>(EMPTY);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useProgressNotes(admissionId, showHistory);
  const create = useCreateProgressNote(admissionId);
  const mayWrite = can('ip:progress-note') && !readOnly;

  return (
    <Section
      focusId="progress_note encounter_diagnosis"
      title="Physician progress notes"
      subtitle="Signed notes are never edited — an amendment creates a new version and preserves the original."
      action={
        <>
          <Button variant="ghost" size="sm" onClick={() => setShowHistory(v => !v)}>
            {showHistory ? 'Hide superseded' : 'Show full history'}
          </Button>
          {mayWrite && !composing && (
            <Button size="sm" data-focus="progress_note:new" onClick={() => { setDraft(EMPTY); setComposing(true); }}>+ Progress note</Button>
          )}
        </>
      }
    >
      {composing && (
        <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3">
          <SoapForm value={draft} onChange={setDraft} />
          {err && <div className="mt-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div>}
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-[11px] text-ink-muted">Saved as a draft — sign it separately.</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setComposing(false); setErr(null); }}>Cancel</Button>
              <Button size="sm" loading={create.isPending} onClick={() => {
                setErr(null);
                create.mutate(draft, {
                  onSuccess: () => setComposing(false),
                  onError: e => setErr(e instanceof ApiError ? e.message : 'Could not save this note.'),
                });
              }}>Save draft</Button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? <LoadingState label="Loading notes…" />
        : !data?.length ? <Empty label="No progress notes yet"
            hint={mayWrite ? 'Use “+ Progress note” to record the first one.' : undefined} />
        : (
          <div className="space-y-2.5">
            {data.map(n => <NoteCard key={n.id} note={n} admissionId={admissionId} readOnly={readOnly} />)}
          </div>
        )}
    </Section>
  );
}
